import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { JournalFormComponent } from '../journal-form/journal-form.component';
import { EtjournalPacketEntry, AddPacketJournalDto } from '@et/typings';
import { Packet } from '@escrowtab/packets-api-client';
import { ProfileDto } from '@escrowtab/v2profile-api-client';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import { EjournalApiService } from '@et/api';
import { EjournalService } from '../../services/ejournal.service';
import { catchError, finalize } from 'rxjs';

@Component({
  selector: 'et-journal-journal-packet-add-edit',
  imports: [JournalFormComponent, MatDialogClose],
  templateUrl: './journal-packet-add-edit.component.html',
  styleUrl: './journal-packet-add-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JournalPacketAddEditComponent {
  private readonly dialogRef = inject(
    MatDialogRef<JournalPacketAddEditComponent>,
  );
  readonly dialogData = inject<{
    packet: Packet;
    profile: ProfileDto;
    eJournal: EtjournalPacketEntry | null;
  }>(MAT_DIALOG_DATA);

  private readonly ejournalApiService = inject(EjournalApiService);
  private readonly ejournalService = inject(EjournalService);

  readonly isLoading = signal<boolean>(false);
  readonly error = signal<string | null>(null);

  onPayloadSubmitted(payload: EtjournalPacketEntry | AddPacketJournalDto) {
    const isEditMode = !!this.dialogData.eJournal;
    const journalPacketId = 'id' in payload ? payload.id : undefined;

    this.isLoading.set(true);
    this.error.set(null);
    this.dialogRef.disableClose = true;

    if (isEditMode && journalPacketId) {
      this.updateEJournal(journalPacketId, payload);
    } else {
      this.createEJournal(payload);
    }
  }

  /**
   * Updates an existing packet journal entry via API and closes the dialog on success.
   *
   * @param journalPacketId ID of the packet journal entry to update
   * @param payload Partial fields to patch (API expects cents for `feeCharged`)
   */
  updateEJournal(
    journalPacketId: string,
    payload: Partial<EtjournalPacketEntry>,
  ) {
    this.ejournalApiService
      .patchPacketJournal(journalPacketId, payload)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          this.dialogRef.disableClose = false;
        }),
      )
      .subscribe((eJournal) => {
        this.ejournalService.setEJournal(eJournal);
        this.dialogRef.close();
      });
  }

  /**
   * Creates a new packet journal entry via API and closes the dialog on success.
   *
   * @param payload Create DTO (API expects cents for `feeCharged`)
   */
  private createEJournal(payload: AddPacketJournalDto): void {
    this.ejournalApiService
      .createPacketJournal(payload)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          this.dialogRef.disableClose = false;
        }),
        catchError((error) => {
          this.error.set('Failed to create eJournal');
          throw error;
        }),
      )
      .subscribe((eJournal) => {
        this.ejournalService.setEJournal(eJournal);
        this.dialogRef.close();
      });
  }
}
